import React, { useState } from "react";
import { ReactComponent as ArrowBackIcon } from "assets/images/common/svgs/arrow_back.svg";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { warningToaster } from "utils/helpers/common/alert-service";
import { productItemDTO } from "utils/helpers/models/product-item/product-item.dto";
import useProductItem from "../../useHooks";

const ProductItemsCreate: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<productItemDTO>();

  const { uploadFile, createProductItems } = useProductItem();

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [deliveryType, setDeliveryType] = useState<"free" | "paid">("free");

  const navigate = useNavigate();

  const goToBack = () => navigate(-1);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const onSubmit = async (data: productItemDTO) => {
    if (!selectedFile) {
      warningToaster("Image is required!");
      return;
    }

    // Upload image
    const formData = new FormData();
    formData.append("file", selectedFile);

    const uploadResponse: any = await uploadFile(formData);
    const imageId = uploadResponse.id;

    const payload: any = {
      itemName: data.itemName,
      cost: Number(data.cost),
      deliveryFee: Number(data.deliveryFee) || 0,
      shouldChargeDeliveryFee: deliveryType === "paid",
      status: true,
      imageId,
    };

    await createProductItems(payload);
  };

  return (
    <div>
      <h1 className="text-base text-primaryText font-bold flex items-center gap-3">
        <span
          onClick={goToBack}
          className="bg-transparentBlueBg w-[44px] h-[44px] rounded-full flex items-center justify-center cursor-pointer"
        >
          <ArrowBackIcon />
        </span>
        Add New Item
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
        <div className="bg-white p-6 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Name */}
            <div className="flex flex-col gap-1 w-full">
              <label className="text-labelText font-semibold">Item name</label>
              <input
                type="text"
                placeholder="Enter Item Name"
                {...register("itemName", {
                  required: "Name is required",
                })}
                className="w-full h-[51px] px-4 border border-lightGrayBorder rounded-lg outline-none"
              />
              {errors.itemName && (
                <span className="text-red-500 text-sm">
                  {errors.itemName.message}
                </span>
              )}
            </div>

            {/* Cost */}
            <div className="flex flex-col gap-1 w-full">
              <label className="text-labelText font-semibold">Cost</label>
              <input
                type="text"
                placeholder="Enter Cost"
                {...register("cost", {
                  required: "Cost is required",
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message: "Enter a valid cost (e.g., 2 or 2.50)",
                  },
                })}
                className="w-full h-[51px] px-4 border border-lightGrayBorder rounded-lg outline-none"
              />
              {errors.cost && (
                <span className="text-red-500 text-sm">
                  {errors.cost.message}
                </span>
              )}
            </div>

            {/* Upload Image */}
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-labelText font-semibold mb-1">
                    Upload Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    id="imageUpload"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <label
                    htmlFor="imageUpload"
                    className="cursor-pointer text-forestText w-[145px] h-[50px] flex justify-center items-center bg-paperWhiteBg rounded-full"
                  >
                    + Upload
                  </label>
                </div>

                {imagePreview && (
                  <div className="relative w-12 h-12 mt-7">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover rounded"
                    />
                    <button
                      type="button"
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                      onClick={() => {
                        setImagePreview(null);
                        setSelectedFile(null);
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Delivery Type Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="text-labelText font-semibold">
                Set Delivery Type
              </label>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="delivery"
                    checked={deliveryType === "free"}
                    onChange={() => setDeliveryType("free")}
                    className="custom-checkbox"
                  />
                  <span className="text-darkTealBlue text-sm font-semibold">
                    Free
                  </span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="delivery"
                    checked={deliveryType === "paid"}
                    onChange={() => setDeliveryType("paid")}
                    className="custom-checkbox"
                  />
                  <span className="text-darkTealBlue text-sm font-semibold">
                    Not Free
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Delivery Fee */}
          {deliveryType === "paid" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              <div className="flex flex-col gap-1 w-full">
                <label className="text-labelText font-semibold">
                  Delivery Fee
                </label>
                <input
                  type="text"
                  placeholder="Enter Delivery Fee"
                  {...register("deliveryFee", {
                    required: "Delivery Fee is required",
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: "Enter a valid delivery fee (e.g., 2 or 2.50)",
                    },
                  })}
                  className="w-full h-[51px] px-4 border border-lightGrayBorder rounded-lg outline-none"
                />
                {errors.deliveryFee && (
                  <span className="text-red-500 text-sm">
                    {errors.deliveryFee.message}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          className="mt-4 bg-darkSlateBlueBg text-white px-6 py-2 rounded"
        >
          Save
        </button>
      </form>
    </div>
  );
};

export default ProductItemsCreate;
